// Crisp Chat Service
// Manages Crisp chat widget integration and session linking

export interface CrispUser {
  nickname?: string
  email?: string
  avatar?: string
  phone?: string
  company?: string
  website?: string
}

export interface CrispSession {
  segments?: string[]
  data?: Record<string, any>
}

// Global Crisp widget interface
declare global {
  interface Window {
    $crisp?: any[]
    CRISP_WEBSITE_ID?: string
  }
}

class CrispService {
  private isInitialized = false
  private isReady = false
  private readyPromise: Promise<boolean> | null = null

  /**
   * Wait for Crisp script to be fully loaded and ready
   * Uses Crisp's recommended approach: wait for crisp:ready event
   */
  private async waitForCrisp(): Promise<boolean> {
    if (typeof window === 'undefined') {
      console.warn('Crisp: Window object not available (SSR environment)')
      return false
    }

    // If already ready, return immediately
    if (this.isReady && window.$crisp) {
      return true
    }

    // If we already have a pending promise, return it
    if (this.readyPromise) {
      return this.readyPromise
    }

    // Create a promise that resolves when Crisp is ready
    this.readyPromise = new Promise<boolean>((resolve) => {
      // First check if Crisp is already loaded
      if (window.$crisp && typeof window.$crisp.push === 'function') {
        // Check if Crisp is already ready by looking for the widget
        const checkReady = () => {
          // Check for Crisp widget in DOM or Crisp API
          const hasCrispAPI = window.$crisp && typeof window.$crisp.push === 'function'
          const hasWidget =
            document.querySelector('#crisp-chatbox') ||
            document.querySelector('.crisp-client') ||
            document.querySelector('[class*="crisp"]')

          if (hasCrispAPI && window.$crisp) {
            // Try to listen for ready event
            try {
              window.$crisp.push([
                'on',
                'crisp:ready',
                () => {
                  this.isReady = true
                  console.log('Crisp: Ready event fired')
                  resolve(true)
                },
              ])
            } catch (error) {
              console.warn('Crisp: Could not register crisp:ready listener:', error)
            }

            // Also check if already ready (event might have fired before we listened)
            setTimeout(() => {
              if (!this.isReady && hasWidget) {
                this.isReady = true
                console.log('Crisp: Widget found in DOM, considering ready')
                resolve(true)
              }
            }, 1000)
          } else {
            // Fallback: just wait a bit and resolve
            setTimeout(() => {
              if (hasCrispAPI) {
                this.isReady = true
                resolve(true)
              } else {
                resolve(false)
              }
            }, 2000)
          }
        }

        checkReady()
      } else {
        // Crisp script not loaded, wait for it
        const maxAttempts = 100
        let attempts = 0

        const checkInterval = setInterval(() => {
          attempts++
          if (window.$crisp && typeof window.$crisp.push === 'function') {
            clearInterval(checkInterval)
            // Now wait for ready event
            setTimeout(() => {
              try {
                if (window.$crisp && typeof window.$crisp.push === 'function') {
                  window.$crisp.push([
                    'on',
                    'crisp:ready',
                    () => {
                      this.isReady = true
                      console.log('Crisp: Ready event fired (delayed)')
                      resolve(true)
                    },
                  ])
                  // Fallback timeout
                  setTimeout(() => {
                    if (!this.isReady && window.$crisp) {
                      this.isReady = true
                      console.log('Crisp: Timeout fallback - considering ready')
                      resolve(true)
                    }
                  }, 3000)
                } else {
                  this.isReady = true
                  resolve(true)
                }
              } catch (error) {
                console.warn('Crisp: Error in ready setup', error)
                this.isReady = true
                resolve(true)
              }
            }, 500)
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval)
            console.warn('Crisp: Widget failed to load within timeout period')
            resolve(false)
          }
        }, 100)
      }
    })

    return this.readyPromise
  }

  /**
   * Initialize Crisp chat widget if not already initialized
   */
  private async ensureInitialized(): Promise<boolean> {
    if (!(await this.waitForCrisp())) {
      return false
    }

    if (!this.isInitialized) {
      this.isInitialized = true
      console.log('Crisp: Service initialized')
    }

    return true
  }

  /**
   * Push a command to Crisp widget
   */
  private async push(command: string, ...args: any[]): Promise<void> {
    if (!(await this.ensureInitialized())) return

    if (!window.$crisp || typeof window.$crisp.push !== 'function') {
      console.warn('Crisp: $crisp.push not available')
      return
    }

    try {
      // Wrap in Promise to catch any synchronous errors from Crisp
      await new Promise<void>((resolve, reject) => {
        try {
          window.$crisp!.push([command, ...args])
          // Crisp push is synchronous, but we resolve after a microtask
          // to allow any errors to surface
          setTimeout(() => resolve(), 0)
        } catch (error) {
          reject(error)
        }
      })
    } catch (error) {
      console.warn('Crisp: Error executing command:', command, error)
      // Don't throw - silently fail to prevent breaking the app
    }
  }

  /**
   * Set user session ID to link with shop
   */
  async setSessionId(sessionId: string): Promise<void> {
    await this.push('set', 'session:identifier', [sessionId])
    console.log('Crisp: Session ID set to:', sessionId)
  }

  /**
   * Set user information
   */
  async setUser(user: CrispUser): Promise<void> {
    if (user.nickname) {
      await this.push('set', 'user:nickname', [user.nickname])
    }
    if (user.email) {
      await this.push('set', 'user:email', [user.email])
    }
    if (user.avatar) {
      await this.push('set', 'user:avatar', [user.avatar])
    }
    if (user.phone) {
      await this.push('set', 'user:phone', [user.phone])
    }
    if (user.company) {
      await this.push('set', 'user:company', [user.company])
    }
    if (user.website) {
      await this.push('set', 'user:website', [user.website])
    }

    console.log('Crisp: User information updated:', user)
  }

  /**
   * Set session data (custom properties)
   */
  async setSessionData(data: Record<string, any>): Promise<void> {
    // Filter out undefined/null/empty values, convert keys to lowercase, and values to strings
    // Crisp's session:data API requires:
    // - Keys in lowercase with only underscores/dashes
    // - String values only (non-empty)
    // - All data set in a single call as array of [key, value] pairs
    const validEntries = Object.entries(data)
      .filter(([_, value]) => {
        // Filter out undefined, null, and empty strings
        if (value === undefined || value === null) return false
        const strValue = String(value).trim()
        return strValue.length > 0
      })
      .map(([key, value]) => {
        // Normalize key: lowercase, replace invalid chars with underscore
        const normalizedKey = key.toLowerCase().replace(/[^a-z0-9_-]/g, '_')
        // Convert value to string and trim
        const stringValue = String(value).trim()
        // Limit value length to prevent issues (Crisp has limits)
        const maxLength = 1000
        const finalValue = stringValue.length > maxLength ? stringValue.substring(0, maxLength) : stringValue
        return [normalizedKey, finalValue]
      })

    if (validEntries.length === 0) {
      console.log('Crisp: No valid session data to set')
      return
    }

    // Set all session data at once (Crisp accepts array of [key, value] pairs)
    // Note: push method spreads args, so we pass validEntries directly
    try {
      await this.push('set', 'session:data', validEntries)
      console.log('Crisp: Session data updated:', validEntries.length, 'entries')
    } catch (error) {
      console.warn('Crisp: Error setting session data:', error)
      // Silently fail - this is not critical for app functionality
    }
  }

  /**
   * Add user to segments for targeting
   */
  async setSegments(segments: string[]): Promise<void> {
    await this.push('set', 'session:segments', [segments])
    console.log('Crisp: User segments set to:', segments)
  }

  /**
   * Show the chat widget
   */
  async show(): Promise<void> {
    // Wait for Crisp to be fully ready
    if (!(await this.waitForCrisp())) {
      console.warn('Crisp: Cannot show widget - not ready')
      return
    }

    if (!window.$crisp) {
      console.warn('Crisp: $crisp object not available')
      return
    }

    try {
      // Only ensure the widget is visible; do not automatically open the chat box.
      window.$crisp.push(['do', 'chat:show'])
    } catch (error) {
      console.error('Crisp: Error showing chat widget:', error)
    }
  }

  /**
   * Hide the chat widget
   */
  async hide(): Promise<void> {
    await this.push('do', 'chat:hide')
  }

  /**
   * Open the chat widget
   */
  async open(): Promise<void> {
    await this.push('do', 'chat:open')
  }

  /**
   * Close the chat widget
   */
  async close(): Promise<void> {
    await this.push('do', 'chat:close')
  }

  /**
   * Reset the chat session
   */
  async reset(): Promise<void> {
    await this.push('do', 'session:reset')
  }

  /**
   * Set up complete shop session with Crisp
   */
  async setupShopSession(options: {
    sessionId: string
    user: CrispUser
    sessionData?: Record<string, any>
    segments?: string[]
  }): Promise<void> {
    const { sessionId, user, sessionData, segments } = options

    // Wait for Crisp to be ready
    if (!(await this.waitForCrisp())) {
      console.error('Crisp: Cannot setup session - widget not loaded')
      return
    }

    // Set session identifier first
    await this.setSessionId(sessionId)

    // Set user information
    await this.setUser(user)

    // Set custom session data if provided
    if (sessionData) {
      await this.setSessionData(sessionData)
    }

    // Set user segments if provided
    if (segments) {
      await this.setSegments(segments)
    }

    // Wait for settings to be applied and ensure the widget bubble is visible.
    await new Promise((resolve) => setTimeout(resolve, 1500))
    await this.show()

    console.log('Crisp: Shop session setup completed for session:', sessionId)
  }

  /**
   * Clear all session data and reset
   */
  async clearSession(): Promise<void> {
    await this.reset()
    console.log('Crisp: Session cleared')
  }
}

// Export singleton instance
export const crispService = new CrispService()
